import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import { toast } from "react-toastify";

const PER_VFC_TOTAL = 413;

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return lines.slice(1).map(line => {
    const cols = line.match(/(".*?"|[^,]+|(?<=,)(?=,))/g) || line.split(",");
    const row = {};
    headers.forEach((h, i) => {
      row[h] = (cols[i] || "").replace(/^"|"$/g, "").trim();
    });
    return row;
  });
}

// Map flexible CSV headers to our standard keys
function normalizeRow(raw) {
  const get = (...keys) => {
    for (const k of keys) {
      const found = Object.keys(raw).find(h => h.includes(k));
      if (found && raw[found]) return raw[found];
    }
    return "";
  };
  return {
    tranId: get("tran", "txn", "ref"),
    valueDate: get("value date", "value"),
    transactionDate: get("transaction date", "date"),
    remarks: get("remark", "narration", "description", "particulars"),
    deposit: get("deposit", "credit", "cr"),
    withdrawal: get("withdrawal", "debit", "dr"),
  };
}

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    PENDING_ASSIGNMENT: { bg: "#FEF9C3", color: "#854D0E", label: "Pending Assignment" },
    PARTIALLY_ASSIGNED: { bg: "#DBEAFE", color: "#1E40AF", label: "Partially Assigned" },
    COMPLETED: { bg: "#DCFCE7", color: "#166534", label: "Completed" },
  };
  const s = map[status] || { bg: "#F3F4F6", color: "#374151", label: status };
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 700, borderRadius: 20,
      padding: "2px 10px", border: `1px solid ${s.color}33`,
    }}>{s.label}</span>
  );
};

// ── Assign Modal ──────────────────────────────────────────────────────────────
const AssignModal = ({ batch, hierarchy, onClose, onSaved }) => {
  const Api = process.env.REACT_APP_API;
  const [circleId, setCircleId] = useState("");
  const [divisionId, setDivisionId] = useState("");
  const [rangeIds, setRangeIds] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const isReassign = batch.status === "COMPLETED";

  // Pre-populate when re-assigning
  useEffect(() => {
    if (!isReassign) return;

    const batchRanges = batch.rangeIds || (batch.rangeId ? [batch.rangeId] : []);
    if (!batchRanges.length) return;

    const normalizedRangeIds = batchRanges.map((r) => r._id || r);

    const firstRange = hierarchy.ranges.find(
      (r) => r._id === normalizedRangeIds[0]
    );

    if (!firstRange) return;

    const divisionIdStr = firstRange.divisionId?._id || firstRange.divisionId;

    const division = hierarchy.divisions.find(
      (d) => d._id === divisionIdStr
    );

    if (!division) return;

    setCircleId(division.circleId?._id || division.circleId);
    setDivisionId(divisionIdStr);
    setRangeIds(normalizedRangeIds);

    const preselected = (batch.vfcIds || []).map((v) => v._id || v);
    setSelected(preselected);
  }, [batch, hierarchy, isReassign]);

  const divisions = hierarchy.divisions.filter(
    (d) => (d.circleId?._id || d.circleId) === circleId
  );

  const ranges = hierarchy.ranges.filter(
    (r) => (r.divisionId?._id || r.divisionId) === divisionId
  );

  const vfcs = hierarchy.vfcs.filter((v) =>
    rangeIds.includes(v.rangeId?._id || v.rangeId)
  );

  const toggleVfc = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const toggleRange = (id) => {
    setSelected([]);

    setRangeIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selected.length === vfcs.length) {
      setSelected([]);
    } else {
      setSelected(vfcs.slice(0, batch.vfcCount).map((v) => v._id));
    }
  };

  const canSave =
    rangeIds.length > 0 &&
    selected.length === batch.vfcCount;

  const save = async () => {
    if (!canSave) return;

    setSaving(true);

    try {
      await axios.put(
        `${Api}/api/bank-upload/batch/${batch._id}/assign`,
        {
          rangeIds,
          vfcIds: selected,
          _reassign: isReassign,
        },
        Authorization()
      );

      toast.success(
        isReassign
          ? "Batch re-assigned successfully!"
          : "Batch assigned successfully!"
      );

      onSaved();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Assignment failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const sel = (label, value, onChange, options, valKey = "_id", labelKey) => (
    <div style={{ marginBottom: 14 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "#4A5568",
          display: "block",
          marginBottom: 5,
        }}
      >
        {label}
      </label>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #E2E8F0",
          borderRadius: 8,
          fontSize: 13,
          color: "#2D3748",
          background: "#fff",
        }}
      >
        <option value="">— Select {label} —</option>

        {options.map((o) => (
          <option key={o[valKey]} value={o[valKey]}>
            {o[labelKey || "name"] ||
              o.circleName ||
              o.divisionName ||
              o.rangeName}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 560,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              {isReassign ? "Re-assign VFCs" : "Assign VFCs"} — {batch.batchRef}
            </div>

            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.7)",
                marginTop: 2,
              }}
            >
              Select exactly {batch.vfcCount} VFC
              {batch.vfcCount > 1 ? "s" : ""} &nbsp;·&nbsp;
              Invoices: {batch.invoiceNos?.join(", ")}

              {isReassign && (
                <span
                  style={{
                    marginLeft: 8,
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: 10,
                    padding: "1px 8px",
                  }}
                >
                  Editing
                </span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              cursor: "pointer",
              width: 30,
              height: 30,
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: 24,
            overflowY: "auto",
            flex: 1,
          }}
        >
          {/* Batch summary */}
          <div
            style={{
              background: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 20,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
            }}
          >
            {[
              ["Transaction ID", batch.transactionId],
              [
                "Deposit Amount",
                `₹ ${Number(batch.depositAmount).toLocaleString("en-IN")}`,
              ],
              ["VFC Count", batch.vfcCount],
              ["Financial Year", batch.financialYear],
            ].map(([l, v]) => (
              <div key={l}>
                <div
                  style={{
                    fontSize: 10,
                    color: "#A0AEC0",
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  {l}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1A202C",
                  }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>

          {/* Selectors */}
          {sel(
            "Circle",
            circleId,
            (id) => {
              setCircleId(id);
              setDivisionId("");
              setRangeIds([]);
              setSelected([]);
            },
            hierarchy.circles,
            "_id",
            "circleName"
          )}

          {circleId &&
            sel(
              "Division",
              divisionId,
              (id) => {
                setDivisionId(id);
                setRangeIds([]);
                setSelected([]);
              },
              divisions,
              "_id",
              "divisionName"
            )}

          {/* Multi Range Selection */}
          {divisionId && (
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#4A5568",
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Range
              </label>

              <div
                style={{
                  border: "1px solid #E2E8F0",
                  borderRadius: 10,
                  maxHeight: 180,
                  overflowY: "auto",
                }}
              >
                {ranges.map((r, i) => (
                  <label
                    key={r._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 14px",
                      borderBottom:
                        i < ranges.length - 1
                          ? "1px solid #F0F4F7"
                          : "none",
                      cursor: "pointer",
                      background: rangeIds.includes(r._id)
                        ? "#F0FFF4"
                        : "#fff",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={rangeIds.includes(r._id)}
                      onChange={() => toggleRange(r._id)}
                      style={{
                        accentColor: "#2E7D32",
                        width: 15,
                        height: 15,
                      }}
                    />

                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#1A202C",
                        }}
                      >
                        {r.rangeName}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* VFC List */}
          {rangeIds.length > 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#4A5568",
                  }}
                >
                  Select VFCs &nbsp;
                  <span
                    style={{
                      color:
                        selected.length === batch.vfcCount
                          ? "#2E7D32"
                          : "#D97706",
                      fontWeight: 700,
                    }}
                  >
                    ({selected.length}/{batch.vfcCount})
                  </span>
                </label>

                {vfcs.length > 0 && (
                  <button
                    onClick={selectAll}
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      border: "1px solid #CBD5E0",
                      borderRadius: 6,
                      background: "#F7FAFC",
                      color: "#2D3748",
                      cursor: "pointer",
                    }}
                  >
                    {selected.length === vfcs.length
                      ? "Deselect All"
                      : "Select All"}
                  </button>
                )}
              </div>

              {vfcs.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px 0",
                    color: "#A0AEC0",
                    fontSize: 13,
                  }}
                >
                  No VFCs found in selected ranges.
                </div>
              ) : (
                <div
                  style={{
                    border: "1px solid #E2E8F0",
                    borderRadius: 10,
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {vfcs.map((v, i) => (
                    <label
                      key={v._id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "9px 14px",
                        borderBottom:
                          i < vfcs.length - 1
                            ? "1px solid #F0F4F7"
                            : "none",
                        cursor: "pointer",
                        background: selected.includes(v._id)
                          ? "#F0FFF4"
                          : "#fff",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(v._id)}
                        onChange={() => toggleVfc(v._id)}
                        disabled={
                          !selected.includes(v._id) &&
                          selected.length >= batch.vfcCount
                        }
                        style={{
                          accentColor: "#2E7D32",
                          width: 15,
                          height: 15,
                        }}
                      />

                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#1A202C",
                          }}
                        >
                          {v.vfcName}
                        </div>

                        {v.vfcCode && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "#A0AEC0",
                            }}
                          >
                            {v.vfcCode}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            gap: 10,
            background: "#F8FAFC",
          }}
        >
          <button
            onClick={save}
            disabled={!canSave || saving}
            style={{
              flex: 1,
              padding: "11px 20px",
              background:
                canSave && !saving
                  ? "linear-gradient(135deg,#1B5E20,#2E7D32)"
                  : "#A5D6A7",
              color: "#fff",
              border: "none",
              borderRadius: 9,
              fontSize: 13,
              fontWeight: 700,
              cursor:
                canSave && !saving
                  ? "pointer"
                  : "not-allowed",
            }}
          >
            {saving
              ? "Saving…"
              : isReassign
                ? `Re-assign ${selected.length}/${batch.vfcCount} VFCs`
                : `Assign ${selected.length}/${batch.vfcCount} VFCs`}
          </button>

          <button
            onClick={onClose}
            style={{
              padding: "11px 20px",
              background: "#fff",
              color: "#4A5568",
              border: "1px solid #E2E8F0",
              borderRadius: 9,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const BankStatementUpload = () => {
  const Api = process.env.REACT_APP_API;
  const [batches, setBatches] = useState([]);
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);  // parsed CSV rows before submit
  const [assignBatch, setAssignBatch] = useState(null);  // batch for modal

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${Api}/api/bank-upload/batches`, Authorization());
      setBatches(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [Api]);

  const fetchHierarchy = useCallback(async () => {
    try {
      const res = await axios.get(`${Api}/api/bank-upload/hierarchy`, Authorization());
      setHierarchy(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [Api]);

  useEffect(() => {
    Promise.all([fetchBatches(), fetchHierarchy()]).finally(() => setLoading(false));
  }, [fetchBatches, fetchHierarchy]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCSV(ev.target.result).map(normalizeRow);
      const depositRows = rows.filter(r => {
        const amt = parseFloat(String(r.deposit || "0").replace(/,/g, ""));
        return amt > 0;
      });
      setPreview(depositRows);
    };
    reader.readAsText(file);
  };

  const submitUpload = async () => {
    if (!preview?.length) return;
    setUploading(true);
    try {
      const res = await axios.post(
        `${Api}/api/bank-upload/upload`,
        { rows: preview },
        Authorization()
      );
      toast.success(res.data.message);
      setPreview(null);
      fetchBatches();
    } catch (err) {
      const msg = err.response?.data?.message || "Upload failed";
      const invalid = err.response?.data?.invalidRows;
      if (invalid) {
        toast.error(`${msg}: ${invalid.map(r => `${r.tranId} (₹${r.deposit})`).join(", ")}`);
      } else {
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
      <div style={{ width: 40, height: 40, border: "4px solid #E2E8F0", borderTopColor: "#2E7D32", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11,
          background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className="mdi mdi-bank-transfer-in" style={{ color: "#fff", fontSize: 22 }} />
        </div>
        <div>
          <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1A202C" }}>Bank Statement Upload</h4>
          <p style={{ margin: 0, fontSize: 12, color: "#718096" }}>
            <span style={{ color: "#2E7D32" }}>JFMC Fee</span> / <span style={{ color: "#2D3748", fontWeight: 600 }}>Bulk Invoice Upload</span>
          </p>
        </div>
      </div>

      {/* Upload card */}
      <div style={{
        background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14,
        overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 24,
      }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", gap: 8 }}>
          <i className="mdi mdi-file-upload-outline" style={{ color: "#2E7D32", fontSize: 18 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: "#2D3748" }}>Upload Bank Statement CSV</span>
        </div>
        <div style={{ padding: 24 }}>
          <p style={{ fontSize: 12, color: "#718096", marginBottom: 16, lineHeight: 1.7 }}>
            Upload a CSV bank statement. Rows with a <strong>Deposit (Cr)</strong> amount that is a
            multiple of <strong>₹{PER_VFC_TOTAL}</strong> will be processed. Each row becomes one
            batch; the deposit amount ÷ {PER_VFC_TOTAL} determines the number of VFC invoices created.
          </p>

          <label style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: `2px dashed ${preview ? "#2E7D32" : "#CBD5E0"}`,
            borderRadius: 12, padding: "28px 20px", cursor: "pointer",
            background: preview ? "#F0FFF4" : "#FAFAFA", transition: "all 0.15s",
            marginBottom: 16,
          }}>
            <i className={`mdi mdi-${preview ? "check-circle" : "cloud-upload-outline"}`}
              style={{ fontSize: 36, color: preview ? "#2E7D32" : "#A0AEC0", marginBottom: 10 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: preview ? "#1B5E20" : "#718096" }}>
              {preview ? `${preview.length} deposit row(s) detected` : "Click to upload CSV"}
            </span>
            <span style={{ fontSize: 11, color: "#A0AEC0", marginTop: 4 }}>
              Supports CSV from most bank statement exports
            </span>
            <input type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: "none" }} />
          </label>

          {/* Preview table */}
          {preview && preview.length > 0 && (() => {
            const validRows = preview.filter(r => parseFloat(String(r.deposit).replace(/,/g, "")) % PER_VFC_TOTAL === 0);
            const invalidRows = preview.length - validRows.length;
            const totalAmount = preview.reduce((s, r) => s + parseFloat(String(r.deposit).replace(/,/g, "")), 0);
            const totalVfcs = validRows.reduce((s, r) => s + Math.round(parseFloat(String(r.deposit).replace(/,/g, "")) / PER_VFC_TOTAL), 0);

            return (
              <div style={{ marginBottom: 16, border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>

                {/* Summary strip */}
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
                  background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
                  padding: "10px 16px", gap: 8,
                }}>
                  {[
                    { label: "Total Rows", value: preview.length },
                    { label: "Valid Rows", value: validRows.length, color: "#A5D6A7" },
                    { label: "Total Amount", value: `₹${Number(totalAmount).toLocaleString("en-IN")}` },
                    { label: "Total VFCs", value: totalVfcs, color: "#A5D6A7" },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: color || "#fff", marginTop: 2 }}>{value}</div>
                    </div>
                  ))}
                </div>

                {invalidRows > 0 && (
                  <div style={{
                    background: "#FEF2F2", borderBottom: "1px solid #FECACA",
                    padding: "7px 16px", fontSize: 12, color: "#DC2626", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <i className="mdi mdi-alert-circle" />
                    {invalidRows} row{invalidRows > 1 ? "s" : ""} with invalid amount (not a multiple of ₹{PER_VFC_TOTAL}) — will be rejected
                  </div>
                )}

                {/* Column headers */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 2fr 1fr 1fr",
                  background: "#F8FAFC", padding: "8px 14px",
                  fontSize: 11, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 0.5,
                  borderBottom: "1px solid #E2E8F0",
                }}>
                  {["Tran ID", "Date", "Remarks", "Deposit (₹)", "VFCs"].map(h => <div key={h}>{h}</div>)}
                </div>

                {preview.map((r, i) => {
                  const amt = parseFloat(String(r.deposit).replace(/,/g, ""));
                  const vfcCount = Math.round(amt / PER_VFC_TOTAL);
                  const isValid = amt % PER_VFC_TOTAL === 0;
                  return (
                    <div key={i} style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr 2fr 1fr 1fr",
                      padding: "9px 14px", fontSize: 12, color: "#2D3748",
                      borderBottom: i < preview.length - 1 ? "1px solid #F0F4F7" : "none",
                      background: isValid ? "#fff" : "#FFF5F5",
                    }}>
                      <div style={{ fontWeight: 600 }}>{r.tranId || "—"}</div>
                      <div style={{ color: "#718096" }}>{r.transactionDate || r.valueDate || "—"}</div>
                      <div style={{ color: "#718096", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.remarks}>{r.remarks || "—"}</div>
                      <div style={{ fontWeight: 700, color: "#1B5E20" }}>₹{Number(amt).toLocaleString("en-IN")}</div>
                      <div>
                        <span style={{
                          background: isValid ? "#E8F5E9" : "#FEE2E2",
                          color: isValid ? "#1B5E20" : "#DC2626",
                          fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 20,
                        }}>
                          {isValid ? `${vfcCount} VFC${vfcCount > 1 ? "s" : ""}` : "Invalid ₹"}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Totals footer row */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 2fr 1fr 1fr",
                  padding: "9px 14px", fontSize: 12,
                  background: "#F0FFF4", borderTop: "2px solid #C8E6C9",
                  fontWeight: 700,
                }}>
                  <div style={{ color: "#1B5E20" }}>{preview.length} rows</div>
                  <div />
                  <div style={{ color: "#1B5E20" }}>TOTAL</div>
                  <div style={{ color: "#1B5E20" }}>₹{Number(totalAmount).toLocaleString("en-IN")}</div>
                  <div style={{ color: "#1B5E20" }}>{totalVfcs} VFCs</div>
                </div>
              </div>
            );
          })()}

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={submitUpload}
              disabled={!preview?.length || uploading}
              style={{
                padding: "10px 24px",
                background: preview?.length && !uploading
                  ? "linear-gradient(135deg,#1B5E20,#2E7D32)"
                  : "#A5D6A7",
                color: "#fff", border: "none", borderRadius: 9,
                fontSize: 13, fontWeight: 700,
                cursor: preview?.length && !uploading ? "pointer" : "not-allowed",
                boxShadow: "0 3px 10px rgba(46,125,50,0.3)",
              }}
            >
              {uploading ? "Processing…" : (() => {
                const validRows = (preview || []).filter(r => parseFloat(String(r.deposit).replace(/,/g, "")) % PER_VFC_TOTAL === 0);
                const totalVfcs = validRows.reduce((s, r) => s + Math.round(parseFloat(String(r.deposit).replace(/,/g, "")) / PER_VFC_TOTAL), 0);
                const totalAmt = validRows.reduce((s, r) => s + parseFloat(String(r.deposit).replace(/,/g, "")), 0);
                return `Create ${validRows.length} Batch${validRows.length !== 1 ? "es" : ""} · ${totalVfcs} VFCs · ₹${Number(totalAmt).toLocaleString("en-IN")}`;
              })()}
            </button>
            {preview && (
              <button
                onClick={() => setPreview(null)}
                style={{
                  padding: "10px 20px", background: "#fff", color: "#4A5568",
                  border: "1px solid #E2E8F0", borderRadius: 9,
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Batch list */}
      <div style={{
        background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14,
        overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid #E2E8F0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="mdi mdi-format-list-bulleted" style={{ color: "#2E7D32", fontSize: 18 }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: "#2D3748" }}>Uploaded Batches</span>
            <span style={{
              background: "#E8F5E9", color: "#2E7D32", fontSize: 11, fontWeight: 700,
              borderRadius: 20, padding: "2px 9px", border: "1px solid #C8E6C9",
            }}>{batches.length}</span>
          </div>
          <button onClick={fetchBatches} style={{
            background: "none", border: "1px solid #E2E8F0", borderRadius: 7,
            color: "#718096", cursor: "pointer", padding: "5px 10px",
            fontSize: 12, display: "flex", alignItems: "center", gap: 5,
          }}>
            <i className="mdi mdi-refresh" style={{ fontSize: 14 }} /> Refresh
          </button>
        </div>

        {batches.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#A0AEC0" }}>
            <i className="mdi mdi-bank-outline" style={{ fontSize: 40, display: "block", marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "#718096" }}>No batches yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Upload a bank statement to create batches.</div>
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div style={{
              display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1.5fr 1.2fr 1fr 120px 140px",
              background: "#F8FAFC", padding: "9px 20px",
              fontSize: 10, fontWeight: 700, color: "#718096",
              textTransform: "uppercase", letterSpacing: 0.5,
              borderBottom: "1px solid #E2E8F0",
            }}>
              {["Batch Ref", "Tran ID", "Date", "Remarks", "Amount", "Invoices", "Status", "Action"].map(h => (
                <div key={h}>{h}</div>
              ))}
            </div>

            {batches.map((b, i) => (
              <div key={b._id} style={{
                display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 1.5fr 1.2fr 1fr 120px 140px",
                padding: "16px 20px", fontSize: 12, color: "#2D3748", alignItems: "center",
                lineHeight: 1.5,
                borderBottom: i < batches.length - 1 ? "1px solid #F0F4F7" : "none",
                background: b.status === "COMPLETED" ? "#FAFFFE" : "#fff",
              }}>
                <div style={{ fontWeight: 700, color: "#1B5E20" }}>{b.batchRef}</div>
                <div style={{ color: "#4A5568" }}>{b.transactionId}</div>
                <div style={{ color: "#718096" }}>
                  {b.transactionDate
                    ? new Date(b.transactionDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                    : "—"}
                </div>

                <div style={{ color: "#718096" }}>
                  {b.transactionRemarks}
                </div>
                <div style={{ fontWeight: 700, textAlign: "center" }}>
                  ₹{Number(b.depositAmount).toLocaleString("en-IN")}
                  <div style={{ fontSize: 10, color: "#A0AEC0", fontWeight: 400 }}>{b.vfcCount} VFC{b.vfcCount > 1 ? "s" : ""}</div>
                </div>
                <div style={{ fontSize: 11, color: "#2E7D32", fontWeight: 600, lineHeight: 1.6 }}>
                  {b.invoiceNos?.join(" · ") || "—"}
                </div>
                <div><StatusBadge status={b.status} /></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {b.status !== "COMPLETED" ? (
                    <button
                      onClick={() => setAssignBatch(b)}
                      style={{
                        padding: "6px 14px",
                        background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
                        color: "#fff", border: "none", borderRadius: 7,
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      Assign VFCs
                    </button>
                  ) : (
                    <>
                      <span style={{ fontSize: 11, color: "#2E7D32", fontWeight: 600 }}>
                        <i className="mdi mdi-check-circle" style={{ marginRight: 4 }} />
                        {b.rangeIds?.length > 0
                          ? b.rangeIds.map((r) => r.rangeName).join(", ")
                          : typeof b.rangeId === "string"
                            ? hierarchy?.ranges?.find((r) => r._id === b.rangeId)?.rangeName || "Done"
                            : b.rangeId?.rangeName || "Done"}
                      </span>
                      <button
                        onClick={() => setAssignBatch(b)}
                        style={{
                          padding: "4px 10px",
                          background: "#fff",
                          color: "#4A5568",
                          border: "1px solid #CBD5E0",
                          borderRadius: 6,
                          fontSize: 10, fontWeight: 600, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 4,
                        }}
                      >
                        <i className="mdi mdi-pencil" style={{ fontSize: 11 }} /> Re-assign
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assign modal */}
      {assignBatch && hierarchy && (
        <AssignModal
          batch={assignBatch}
          hierarchy={hierarchy}
          onClose={() => setAssignBatch(null)}
          onSaved={fetchBatches}
        />
      )}
    </div>
  );
};

export default BankStatementUpload;
